// App.jsx
import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";
import { HelmetProvider } from "react-helmet-async";
import "./styles/global.css";
import "./styles/website.css";

// Import Website Pages
import Home from "./pages/website/Home";
import Services from "./pages/website/Services";
import Ecommerce from "./pages/website/Ecommerce";
import About from "./pages/website/About";
import Contact from "./pages/website/Contact";
import Privacy from "./pages/website/Privacy";
import EmailUnsubscribe from "./pages/website/EmailUnsubscribe";
import ThankYou from "./pages/website/ThankYou";

// Import Auth Pages
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import VerifyEmail from "./pages/auth/VerifyEmail";

// Import Client Pages
import ClientDashboard from "./pages/client/Dashboard/ClientDashboard";
import RequestStore from "./pages/client/StoreRequest/RequestStore";
import PackageSelection from "./pages/client/StoreRequest/PackageSelection";
import RequestStatus from "./pages/client/StoreRequest/RequestStatus";
import WebsiteRequestStatus from "./pages/client/StoreRequest/WebsiteRequestStatus";
import WebsiteRequests from "./pages/client/StoreRequest/WebsiteRequests";
import ProfileSettings from "./pages/client/Profile/ProfileSettings";
import SubscriptionDetails from "./pages/client/Subscription/SubscriptionDetails";
import CommunicationCenter from "./pages/common/CommunicationCenter";
import Product from "./pages/client/Products/Products";
import WebsiteSupportWidget from "./components/support/WebsiteSupportWidget";

const GuestTicketUrlSanitizer = () => {
  const location = useLocation();
  if (location.pathname === "/contact") {
    const params = new URLSearchParams(window.location.search);
    const ticket = params.get("ticket");
    const key = params.get("key");
    if (ticket && key) {
      window.tooraPendingGuestTicket = { ticket, key };
      window.history.replaceState(
        window.history.state,
        "",
        `${window.location.pathname}${window.location.hash}`,
      );
    }
  }
  return null;
};

// Import Admin Pages
import AdminDashboard from "./pages/admin/Dashboard/AdminDashboard";
import RequestList from "./pages/admin/StoreRequests/RequestList";
import RequestDetail from "./pages/admin/StoreRequests/RequestDetail";
import WebsiteRequestList from "./pages/admin/WebsiteRequests/WebsiteRequestList";
import WebsiteRequestDetail from "./pages/admin/WebsiteRequests/WebsiteRequestDetail";
import ClientList from "./pages/admin/Clients/ClientList";
import StoreOverview from "./pages/admin/Stores/StoreOverview";
import PackageManager from "./pages/admin/Packages/PackageManager";
import WebsitePackageManager from "./pages/admin/Packages/WebsitePackageManager";
import WebsiteOnboarding from "./pages/client/StoreRequest/WebsiteOnboarding";
import PaymentList from "./pages/admin/Payments/PaymentList";
import AuditLogs from "./pages/admin/Audit/AuditLogs";
import EmailSettings from "./pages/admin/Settings/EmailSettings";
import StoreDatabaseSettings from "./pages/admin/Settings/StoreDatabaseSettings";
import NginxServerSettings from "./pages/admin/Settings/NginxServerSettings";
import OnboardingControl from "./pages/admin/onboarding-control/OnboardingControl";
import WebsiteOnboardingControl from "./pages/admin/onboarding-control/WebsiteOnboardingControl";
import ContractLibrary from "./pages/admin/Contracts/ContractLibrary";
import PlatformAnalytics from "./pages/admin/Analytics/PlatformAnalytics";
import AdminProfile from "./pages/admin/Profile/AdminProfile";
import { analyticsApi } from "./api/analyticsApi";
import { getAnalyticsVisitorId } from "./utils/analyticsSession";

// Layout Components
import WebsiteLayout from "./components/common/Layout/WebsiteLayout";
import ClientLayout from "./components/common/Layout/ClientLayout";
import AdminLayout from "./components/common/Layout/AdminLayout";

// TikTok Pixel Component
const TikTokPixel = () => {
  const location = useLocation();
  const pixelLoaded = React.useRef(false);

  React.useEffect(() => {
    if (
      location.pathname === "/contact" ||
      location.pathname === "/email-preferences/unsubscribe" ||
      pixelLoaded.current
    ) {
      return;
    }
    pixelLoaded.current = true;

    // TikTok Pixel Code
    !(function (w, d, t) {
      w.TiktokAnalyticsObject = t;
      var ttq = (w[t] = w[t] || []);
      ttq.methods = [
        "page",
        "track",
        "identify",
        "instances",
        "debug",
        "on",
        "off",
        "once",
        "ready",
        "alias",
        "group",
        "enableCookie",
        "disableCookie",
        "holdConsent",
        "revokeConsent",
        "grantConsent",
      ];
      ttq.setAndDefer = function (t, e) {
        t[e] = function () {
          t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
        };
      };
      for (var i = 0; i < ttq.methods.length; i++)
        ttq.setAndDefer(ttq, ttq.methods[i]);
      ttq.instance = function (t) {
        for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++)
          ttq.setAndDefer(e, ttq.methods[n]);
        return e;
      };
      ttq.load = function (e, n) {
        var r = "https://analytics.tiktok.com/i18n/pixel/events.js",
          o = n && n.partner;
        ttq._i = ttq._i || {};
        ttq._i[e] = [];
        ttq._i[e]._u = r;
        ttq._t = ttq._t || {};
        ttq._t[e] = +new Date();
        ttq._o = ttq._o || {};
        ttq._o[e] = n || {};
        n = document.createElement("script");
        n.type = "text/javascript";
        n.async = !0;
        n.src = r + "?sdkid=" + e + "&lib=" + t;
        e = document.getElementsByTagName("script")[0];
        e.parentNode.insertBefore(n, e);
      };

      ttq.load("DAANHLRC77UEOA3OA9QG");
      ttq.page();
    })(window, document, "ttq");
  }, [location.pathname]);

  return null;
};

const ANALYTICS_PUBLIC_PATHS = new Set([
  "/",
  "/services",
  "/ecommerce",
  "/about",
  "/contact",
  "/privacy",
  "/thank-you",
]);

const getAnalyticsBrowser = (userAgent) => {
  if (/Edg\//.test(userAgent)) return "Edge";
  if (/Firefox\//.test(userAgent)) return "Firefox";
  if (/Chrome\//.test(userAgent) && !/Edg\//.test(userAgent)) return "Chrome";
  if (/Safari\//.test(userAgent) && !/Chrome\//.test(userAgent)) return "Safari";
  return "Other";
};

const WebsiteAnalyticsTracker = () => {
  const location = useLocation();
  const { user, loading } = useAuth();

  React.useEffect(() => {
    if (
      loading ||
      user ||
      !ANALYTICS_PUBLIC_PATHS.has(location.pathname)
    ) {
      return;
    }

    let visitorId;
    try {
      visitorId = getAnalyticsVisitorId();
      const pageKey = `${location.key}:${location.pathname}`;
      if (sessionStorage.getItem("toora-analytics-last-page") === pageKey) {
        return;
      }
      sessionStorage.setItem("toora-analytics-last-page", pageKey);
    } catch (storageError) {
      console.warn("Anonymous website analytics unavailable:", storageError);
      return;
    }

    const userAgent = navigator.userAgent;
    const device = /iPad|Tablet/i.test(userAgent)
      ? "tablet"
      : /Mobi|Android/i.test(userAgent)
        ? "mobile"
        : "desktop";
    const params = new URLSearchParams(location.search);
    let source = params.get("utm_source") || "Direct";
    if (source === "Direct" && document.referrer) {
      try {
        const referrer = new URL(document.referrer);
        if (referrer.host !== window.location.host) {
          source = referrer.hostname;
        }
      } catch (referrerError) {
        console.warn("Could not read website analytics referrer:", referrerError);
      }
    }

    analyticsApi
      .trackPageview({
        visitorId,
        pagePath: location.pathname,
        source,
        device,
        browser: getAnalyticsBrowser(userAgent),
        campaign: params.get("utm_campaign") || "",
      })
      .catch((analyticsError) => {
        console.error("Could not record anonymous website analytics:", analyticsError);
      });
  }, [loading, location.key, location.pathname, location.search, user]);

  return null;
};

// Auth Guard Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (allowedRoles && !allowedRoles.includes(user.Role)) {
    return <Navigate to="/dashboard" />;
  }

  if (
    user.Role === "client" &&
    user.MustChangePassword &&
    location.pathname !== "/change-temporary-password"
  ) {
    return <Navigate to="/change-temporary-password" replace />;
  }

  return children;
};

const TemporaryPasswordRedirect = () => {
  const navigate = useNavigate();

  React.useEffect(() => {
    const redirectToPasswordChange = () => {
      navigate("/change-temporary-password", { replace: true });
    };
    window.addEventListener(
      "auth:password-change-required",
      redirectToPasswordChange,
    );
    return () =>
      window.removeEventListener(
        "auth:password-change-required",
        redirectToPasswordChange,
      );
  }, [navigate]);

  return null;
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <NotificationProvider>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <TemporaryPasswordRedirect />
            <GuestTicketUrlSanitizer />
            <TikTokPixel />
            <WebsiteAnalyticsTracker />
            <WebsiteSupportWidget />
            <Routes>
              {/* Website Routes - Public */}
              <Route
                path="/"
                element={
                  <WebsiteLayout>
                    <Home />
                  </WebsiteLayout>
                }
              />
              <Route
                path="/services"
                element={
                  <WebsiteLayout>
                    <Services />
                  </WebsiteLayout>
                }
              />
              <Route
                path="/ecommerce"
                element={
                  <WebsiteLayout>
                    <Ecommerce />
                  </WebsiteLayout>
                }
              />
              <Route
                path="/about"
                element={
                  <WebsiteLayout>
                    <About />
                  </WebsiteLayout>
                }
              />
              <Route
                path="/contact"
                element={
                  <WebsiteLayout>
                    <Contact />
                  </WebsiteLayout>
                }
              />
              <Route
                path="/privacy"
                element={
                  <WebsiteLayout>
                    <Privacy />
                  </WebsiteLayout>
                }
              />
              <Route
                path="/email-preferences/unsubscribe"
                element={<EmailUnsubscribe />}
              />
              <Route
                path="/thank-you"
                element={
                  <WebsiteLayout>
                    <ThankYou />
                  </WebsiteLayout>
                }
              />

              {/* Auth Routes - Public */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route
                path="/reset-password/:token"
                element={<ResetPassword />}
              />
              <Route
                path="/change-temporary-password"
                element={
                  <ProtectedRoute allowedRoles={["client"]}>
                    <ResetPassword forceChange />
                  </ProtectedRoute>
                }
              />

              {/* Client Routes - Protected */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute allowedRoles={["client"]}>
                    <ClientLayout>
                      <ClientDashboard />
                    </ClientLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/request-store"
                element={
                  <ProtectedRoute allowedRoles={["client"]}>
                    <ClientLayout>
                      <RequestStore />
                    </ClientLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/website-onboarding"
                element={
                  <ProtectedRoute allowedRoles={["client"]}>
                    <ClientLayout>
                      <WebsiteOnboarding />
                    </ClientLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/packages"
                element={
                  <ProtectedRoute allowedRoles={["client"]}>
                    <ClientLayout>
                      <PackageSelection />
                    </ClientLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/request-status/:id"
                element={
                  <ProtectedRoute allowedRoles={["client"]}>
                    <ClientLayout>
                      <RequestStatus />
                    </ClientLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/website-request-status/:id"
                element={
                  <ProtectedRoute allowedRoles={["client"]}>
                    <ClientLayout>
                      <WebsiteRequestStatus />
                    </ClientLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/website-requests"
                element={
                  <ProtectedRoute allowedRoles={["client"]}>
                    <ClientLayout>
                      <WebsiteRequests />
                    </ClientLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/profile"
                element={
                  <ProtectedRoute allowedRoles={["client"]}>
                    <ClientLayout>
                      <ProfileSettings />
                    </ClientLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/messages"
                element={<Navigate to="/support" replace />}
              />
              <Route
                path="/subscription"
                element={
                  <ProtectedRoute allowedRoles={["client"]}>
                    <ClientLayout>
                      <SubscriptionDetails />
                    </ClientLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products"
                element={
                  <ProtectedRoute allowedRoles={["client"]}>
                    <ClientLayout>
                      <Product />
                    </ClientLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/support"
                element={
                  <ProtectedRoute allowedRoles={["client"]}>
                    <ClientLayout>
                      <CommunicationCenter />
                    </ClientLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute allowedRoles={["client"]}>
                    <ClientLayout>
                      <CommunicationCenter />
                    </ClientLayout>
                  </ProtectedRoute>
                }
              />

              {/* Admin Routes - Protected */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminLayout>
                      <AdminDashboard />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/analysis"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminLayout>
                      <PlatformAnalytics />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/profile"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminLayout>
                      <AdminProfile />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/requests"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminLayout>
                      <RequestList />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/requests/:id"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminLayout>
                      <RequestDetail />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/website-requests"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminLayout>
                      <WebsiteRequestList />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/website-requests/:id"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminLayout>
                      <WebsiteRequestDetail />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/communication"
                element={
                  <ProtectedRoute allowedRoles={["client"]}>
                    <ClientLayout>
                      <CommunicationCenter />
                    </ClientLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/support"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminLayout>
                      <CommunicationCenter />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/notifications"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminLayout>
                      <CommunicationCenter />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/communication"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminLayout>
                      <CommunicationCenter />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/clients"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminLayout>
                      <ClientList />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/stores"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminLayout>
                      <StoreOverview />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/packages"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminLayout>
                      <PackageManager />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/website-packages"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminLayout>
                      <WebsitePackageManager />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/payments"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminLayout>
                      <PaymentList />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/audit"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminLayout>
                      <AuditLogs />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/email"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminLayout>
                      <EmailSettings />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/store-database"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminLayout>
                      <StoreDatabaseSettings />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/nginx-server"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminLayout>
                      <NginxServerSettings />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />
              {/* Onboarding Control - Admin Only */}
              <Route
                path="/admin/onboarding-control"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminLayout>
                      <OnboardingControl />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/website-onboarding-control"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminLayout>
                      <WebsiteOnboardingControl />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/contracts"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminLayout>
                      <ContractLibrary />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />

              {/* Fallback Route */}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Router>
        </NotificationProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
